// MCCD (Form 4) editor — the doctor's structured certification of the cause of
// death. Backs both the doctor's "Certify Death" dialog (DischargeStageTracker)
// and the Death Register's edit dialog.
//
// The cause of death is a causal SEQUENCE, not a sentence: Part I (a) immediate
// -> (b) antecedent -> (c) underlying, each with an interval between onset and
// death, plus Part II contributing conditions. The UNDERLYING cause is the last
// filled Part I line — the only one tabulated — and it is echoed live so the
// certifier can see what they're actually stating. The backend validates the
// rest (interval per line, no mode of dying as the underlying cause).
//
// Contiguity is enforced by construction: line (b) is disabled until (a) has a
// condition, (c) until (b) does — so a gap can never be created.
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { useIpdData } from "../context/IpdDataContext";
import { MANNER_OF_DEATH_OPTIONS } from "../api/dischargeProcessApi";

const PART1_LABELS = [
  { key: "a", label: "(a) Immediate cause", hint: "The disease/injury that directly caused death — not the mode of dying" },
  { key: "b", label: "(b) Antecedent cause", hint: "due to (or as a consequence of)" },
  { key: "c", label: "(c) Underlying cause", hint: "due to (or as a consequence of)" },
];

const emptyRow = () => ({ condition: "", interval: "" });

export const EMPTY_MCCD = {
  part1: [emptyRow(), emptyRow(), emptyRow()],
  part2: [emptyRow()],
  mannerOfDeath: "",
  certifyingDoctorId: "",
  deathCertificateNumber: "",
  injuryDescription: "",
  pregnancyAssociated: "", // "" (N/A) | "yes" | "no"
  deliveryOccurred: "",
  timeOfDeath: "",
};

// ISO -> "YYYY-MM-DDTHH:mm" (local) for a datetime-local input.
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

const triToStr = (v) => (v === true ? "yes" : v === false ? "no" : "");

// Build the form state from a DischargeProcess row.
export function mccdFormFromProcess(dp) {
  const p1 = Array.isArray(dp?.mccd_part1) ? dp.mccd_part1 : [];
  const p2 = Array.isArray(dp?.mccd_part2) ? dp.mccd_part2 : [];
  const part1 = [0, 1, 2].map((i) => ({
    condition: p1[i]?.condition || "",
    interval: p1[i]?.interval || "",
  }));
  return {
    part1,
    part2: p2.length ? p2.map((r) => ({ condition: r.condition || "", interval: r.interval || "" })) : [emptyRow()],
    mannerOfDeath: dp?.manner_of_death || "",
    certifyingDoctorId: dp?.certifying_doctor_id || "",
    deathCertificateNumber: dp?.death_certificate_number || "",
    injuryDescription: dp?.mccd_injury_description || "",
    pregnancyAssociated: triToStr(dp?.mccd_pregnancy_associated),
    deliveryOccurred: triToStr(dp?.mccd_delivery_occurred),
    timeOfDeath: toLocalInput(dp?.death_datetime),
  };
}

// Map the form state to the updateMccd() argument shape. Empty Part I/II lines
// are dropped (the backend also cleans, but sending tidy data keeps line labels
// honest). Time of death is padded to seconds like the other datetime-local uses.
export function mccdFormToPayload(form) {
  const clean = (rows) =>
    (rows || [])
      .map((r) => ({ condition: (r.condition || "").trim(), interval: (r.interval || "").trim() }))
      .filter((r) => r.condition);
  const raw = form.timeOfDeath;
  const timeOfDeath = raw ? (raw.length === 16 ? `${raw}:00` : raw) : undefined;
  const tri = (v) => (v === "yes" ? true : v === "no" ? false : null);
  return {
    part1: clean(form.part1),
    part2: clean(form.part2),
    mannerOfDeath: form.mannerOfDeath || "",
    certifyingDoctorId: form.certifyingDoctorId || "",
    deathCertificateNumber: form.deathCertificateNumber || "",
    injuryDescription: form.injuryDescription || "",
    pregnancyAssociated: tri(form.pregnancyAssociated),
    deliveryOccurred: tri(form.deliveryOccurred),
    timeOfDeath,
  };
}

export default function MccdForm({ value, onChange, disabled = false }) {
  const { getAvailableDoctors } = useIpdData();
  const [departments, setDepartments] = useState([]);
  const [department, setDepartment] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);

  useEffect(() => {
    const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL;
    const token = sessionStorage.getItem("accessToken") || "";
    axios
      .get(`${BASE}get_all_medical_departments/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data?.department_list) {
          setDepartments(res.data.department_list.map((d) => d.department));
        }
      })
      .catch(() => setDepartments([]));
  }, []);

  const handleDepartmentChange = async (dept) => {
    setDepartment(dept);
    onChange({ certifyingDoctorId: "" });
    if (!dept) {
      setDoctors([]);
      return;
    }
    setDoctorsLoading(true);
    const result = await getAvailableDoctors(dept, { assignedDoctorsOnly: false });
    setDoctors(result.data || []);
    setDoctorsLoading(false);
  };

  const setField = (field) => (e) => onChange({ [field]: e.target.value });

  const setPart1 = (idx, key, val) => {
    const part1 = value.part1.map((r, i) => (i === idx ? { ...r, [key]: val } : r));
    // Clearing a condition clears the lines below it (contiguity).
    if (key === "condition" && !val.trim()) {
      for (let j = idx + 1; j < part1.length; j += 1) part1[j] = emptyRow();
    }
    onChange({ part1 });
  };

  const setPart2 = (idx, key, val) => {
    onChange({ part2: value.part2.map((r, i) => (i === idx ? { ...r, [key]: val } : r)) });
  };
  const addPart2 = () => onChange({ part2: [...value.part2, emptyRow()] });
  const removePart2 = (idx) =>
    onChange({ part2: value.part2.filter((_, i) => i !== idx) || [emptyRow()] });

  // Underlying cause = last Part I line with a condition. Echoed live.
  const underlying = [...value.part1].reverse().find((r) => r.condition.trim())?.condition || null;

  const isNonNatural = value.mannerOfDeath && value.mannerOfDeath !== "natural";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Part I — the causal sequence */}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Part I — cause of death sequence
        </Typography>
        {PART1_LABELS.map((line, idx) => {
          const prevFilled = idx === 0 || value.part1[idx - 1].condition.trim();
          const rowDisabled = disabled || !prevFilled;
          return (
            <Box key={line.key} sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 1 }}>
              <TextField
                sx={{ flex: 3 }}
                size="small"
                label={line.label}
                placeholder={line.hint}
                disabled={rowDisabled}
                value={value.part1[idx].condition}
                onChange={(e) => setPart1(idx, "condition", e.target.value)}
              />
              <TextField
                sx={{ flex: 1 }}
                size="small"
                label="Interval"
                placeholder="e.g. 2 days"
                disabled={rowDisabled}
                value={value.part1[idx].interval}
                onChange={(e) => setPart1(idx, "interval", e.target.value)}
              />
            </Box>
          );
        })}
        {underlying && (
          <Typography variant="caption" color="primary" component="div">
            Underlying cause (the one tabulated): <strong>{underlying}</strong>
          </Typography>
        )}
      </Box>

      {/* Part II — contributing conditions */}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Part II — other significant conditions
        </Typography>
        {value.part2.map((row, idx) => (
          <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 1 }}>
            <TextField
              sx={{ flex: 3 }}
              size="small"
              label="Condition"
              disabled={disabled}
              value={row.condition}
              onChange={(e) => setPart2(idx, "condition", e.target.value)}
            />
            <TextField
              sx={{ flex: 1 }}
              size="small"
              label="Interval"
              disabled={disabled}
              value={row.interval}
              onChange={(e) => setPart2(idx, "interval", e.target.value)}
            />
            <IconButton
              size="small"
              disabled={disabled || value.part2.length <= 1}
              onClick={() => removePart2(idx)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Button size="small" startIcon={<AddIcon />} disabled={disabled} onClick={addPart2}>
          Add condition
        </Button>
      </Box>

      {/* Manner + circumstances */}
      <TextField
        select
        fullWidth
        size="small"
        label="Manner of Death *"
        disabled={disabled}
        value={value.mannerOfDeath}
        onChange={setField("mannerOfDeath")}
      >
        <MenuItem value="">
          <em>Not recorded</em>
        </MenuItem>
        {MANNER_OF_DEATH_OPTIONS.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>

      {isNonNatural && (
        <TextField
          fullWidth
          size="small"
          multiline
          rows={2}
          label="How did the injury occur?"
          disabled={disabled}
          value={value.injuryDescription}
          onChange={setField("injuryDescription")}
        />
      )}

      <Box sx={{ display: "flex", gap: 1.5 }}>
        <TextField
          select
          fullWidth
          size="small"
          label="Pregnancy-associated? (female)"
          disabled={disabled}
          value={value.pregnancyAssociated}
          onChange={setField("pregnancyAssociated")}
        >
          <MenuItem value=""><em>N/A</em></MenuItem>
          <MenuItem value="no">No</MenuItem>
          <MenuItem value="yes">Yes</MenuItem>
        </TextField>
        {value.pregnancyAssociated === "yes" && (
          <TextField
            select
            fullWidth
            size="small"
            label="Was there a delivery?"
            disabled={disabled}
            value={value.deliveryOccurred}
            onChange={setField("deliveryOccurred")}
          >
            <MenuItem value=""><em>—</em></MenuItem>
            <MenuItem value="no">No</MenuItem>
            <MenuItem value="yes">Yes</MenuItem>
          </TextField>
        )}
      </Box>

      {/* Certifier + timing */}
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <TextField
          select
          fullWidth
          size="small"
          label="Department"
          disabled={disabled}
          value={department}
          onChange={(e) => handleDepartmentChange(e.target.value)}
        >
          <MenuItem value=""><em>Select</em></MenuItem>
          {departments.map((d) => (
            <MenuItem key={d} value={d}>{d}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          size="small"
          label="Certifying Doctor *"
          disabled={disabled || !department || doctorsLoading}
          value={value.certifyingDoctorId}
          onChange={setField("certifyingDoctorId")}
        >
          <MenuItem value=""><em>{doctorsLoading ? "Loading…" : "Select"}</em></MenuItem>
          {doctors.map((doc) => (
            <MenuItem key={doc.doctor_id} value={String(doc.doctor_id)}>{doc.name}</MenuItem>
          ))}
        </TextField>
      </Box>

      <TextField
        fullWidth
        size="small"
        type="datetime-local"
        label="Time of Death *"
        disabled={disabled}
        InputLabelProps={{ shrink: true }}
        value={value.timeOfDeath}
        onChange={setField("timeOfDeath")}
        helperText="Must be earlier than the physical discharge time."
      />

      <TextField
        fullWidth
        size="small"
        label="Registrar's Death Certificate No. (if issued)"
        disabled={disabled}
        value={value.deathCertificateNumber}
        onChange={setField("deathCertificateNumber")}
        helperText="The number from the Registrar — often arrives days later. Optional."
      />
    </Box>
  );
}
